import React from 'react';
import { Card, Spin, Alert, Empty } from 'antd';
import { Column } from '@ant-design/charts';
import { useProductionTrends } from '../../../hooks/useManufacturingDashboard';

interface ProductionTrendsChartProps {
  days: number;
}

const ProductionTrendsChart: React.FC<ProductionTrendsChartProps> = ({ days }) => {
  const { data: trends, isLoading, error } = useProductionTrends(days);

  if (error) {
    return (
      <Card title="Production Trends">
        <Alert
          message="Error Loading Production Trends"
          description={error.message}
          type="error"
          showIcon
        />
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card title="Production Trends">
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!trends || trends.length === 0) {
    return (
      <Card title="Production Trends">
        <Empty description="No production data available" />
      </Card>
    );
  }

  // Transform data for dual-axis chart
  const chartData = trends.flatMap(trend => [
    { date: trend.date, type: 'Planned', value: trend.planned },
    { date: trend.date, type: 'Produced', value: trend.produced },
    { date: trend.date, type: 'Rejected', value: trend.rejected },
  ]);

  const config: any = {
    data: chartData,
    xField: 'date',
    yField: 'value',
    seriesField: 'type',
    isGroup: true,
    columnStyle: {
      radius: [4, 4, 0, 0],
    },
    color: ({ type }: any) => {
      if (type === 'Planned') return '#145A54';
      if (type === 'Produced') return '#52c41a';
      return '#ff4d4f';
    },
    legend: {
      position: 'top' as const,
    },
    label: {
      position: 'top' as const,
      style: {
        fill: '#000000',
        opacity: 0.6,
        fontSize: 10,
      },
    },
    xAxis: {
      label: {
        autoHide: true,
        autoRotate: false,
      },
    },
    meta: {
      value: {
        alias: 'Quantity',
      },
      date: {
        alias: 'Date',
      },
    },
    tooltip: {
      shared: true,
    },
  };

  return (
    <Card
      title="Production Trends"
      extra={
        <span style={{ fontSize: 12, color: '#8c8c8c' }}>
          Last {days} days
        </span>
      }
    >
      <Column {...config} />
      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-around', borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 20, fontWeight: 500, color: '#145A54' }}>
            {trends.reduce((sum, t) => sum + t.planned, 0)}
          </div>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>Total Planned</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 20, fontWeight: 500, color: '#52c41a' }}>
            {trends.reduce((sum, t) => sum + t.produced, 0)}
          </div>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>Total Produced</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 20, fontWeight: 500, color: '#ff4d4f' }}>
            {trends.reduce((sum, t) => sum + t.rejected, 0)}
          </div>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>Total Rejected</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 20, fontWeight: 500, color: '#722ed1' }}>
            {(trends.reduce((sum, t) => sum + t.efficiency, 0) / trends.length).toFixed(1)}%
          </div>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>Avg Efficiency</div>
        </div>
      </div>
    </Card>
  );
};

export default ProductionTrendsChart;
